import { Component } from 'react'
import { toast } from 'react-toastify'
import {
    createPushRule,
    deletePushRule,
    getPushPublicKey,
    listPushMetrics,
    listPushRules,
    PushMetric,
    PushMetricOption,
    PushRule,
    sendPushTest,
    subscribePush,
    unsubscribePush,
    updatePushRule,
} from '../../api/push'
import { getString } from '../../locale'

type Props = {
    clientId: string
    monitoringEnabled?: boolean
}

type State = {
    loading: boolean
    supported: boolean
    subscribed: boolean
    metrics: PushMetricOption[]
    rules: PushRule[]
    selectedMetric?: PushMetric
    thresholdMinutes: number
    cooldownMinutes: number
    label: string
    busy: boolean
}

const DEFAULT_THRESHOLD_MINUTES = 30
const DEFAULT_COOLDOWN_MINUTES = 60
type PushMetricLocaleKey =
    | 'push.metric.cooling_unit_active_time'
    | 'push.metric.cooling_unit_inactive_time'
    | 'push.metric.pump_active_time'
    | 'push.metric.pump_inactive_time'
    | 'push.metric.boiler_active_time'
    | 'push.metric.boiler_inactive_time'
    | 'push.metric.fan_active_time'
    | 'push.metric.fan_inactive_time'
    | 'push.metric.circulation_pump_active_time'
    | 'push.metric.circulation_pump_inactive_time'

const METRIC_LOCALE_KEY_MAP: Record<PushMetric, PushMetricLocaleKey> = {
    cooling_unit_active_time: 'push.metric.cooling_unit_active_time',
    cooling_unit_inactive_time: 'push.metric.cooling_unit_inactive_time',
    pump_active_time: 'push.metric.pump_active_time',
    pump_inactive_time: 'push.metric.pump_inactive_time',
    boiler_active_time: 'push.metric.boiler_active_time',
    boiler_inactive_time: 'push.metric.boiler_inactive_time',
    fan_active_time: 'push.metric.fan_active_time',
    fan_inactive_time: 'push.metric.fan_inactive_time',
    circulation_pump_active_time: 'push.metric.circulation_pump_active_time',
    circulation_pump_inactive_time: 'push.metric.circulation_pump_inactive_time',
}

export class PushRulesPanel extends Component<Props, State> {
    state: State = {
        loading: true,
        supported: false,
        subscribed: false,
        metrics: [],
        rules: [],
        selectedMetric: undefined,
        thresholdMinutes: DEFAULT_THRESHOLD_MINUTES,
        cooldownMinutes: DEFAULT_COOLDOWN_MINUTES,
        label: '',
        busy: false,
    }

    async componentDidMount() {
        await this.bootstrap()
    }

    private async bootstrap() {
        const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window
        if (!supported) {
            this.setState({ loading: false, supported: false })
            return
        }
        try {
            const metricsResult = await listPushMetrics()
            const selectedMetric = metricsResult.data[0]?.value
            const sub = await this.getExistingSubscription()
            const rulesResult = await listPushRules(this.props.clientId)
            this.setState({
                loading: false,
                supported: true,
                metrics: metricsResult.data,
                selectedMetric,
                subscribed: !!sub,
                rules: rulesResult.data,
            })
        } catch (error) {
            this.setState({ loading: false, supported: true })
            toast.error((error as Error).message)
        }
    }

    private getMetricLabel(metric: PushMetric) {
        return getString(METRIC_LOCALE_KEY_MAP[metric])
    }

    private async getOrRegisterWorker(): Promise<ServiceWorkerRegistration> {
        return navigator.serviceWorker.register('/sw-push.js')
    }

    private async getExistingSubscription(): Promise<PushSubscription | null> {
        const registration = await this.getOrRegisterWorker()
        return registration.pushManager.getSubscription()
    }

    private urlBase64ToUint8Array(base64String: string): Uint8Array {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
        const rawData = window.atob(base64)
        const outputArray = new Uint8Array(rawData.length)
        for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i)
        return outputArray
    }

    onSubscribe = async () => {
        if (this.state.busy) return
        this.setState({ busy: true })
        try {
            const registration = await this.getOrRegisterWorker()
            const keyResult = await getPushPublicKey()
            let sub = await registration.pushManager.getSubscription()
            if (!sub) {
                sub = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: this.urlBase64ToUint8Array(keyResult.data.publicKey),
                })
            }
            await subscribePush(sub)
            this.setState({ subscribed: true })
            toast.success(getString('push.subscribed'))
        } catch (error) {
            toast.error((error as Error).message || getString('push.subscribe_failed'))
        } finally {
            this.setState({ busy: false })
        }
    }

    onUnsubscribe = async () => {
        if (this.state.busy) return
        this.setState({ busy: true })
        try {
            const sub = await this.getExistingSubscription()
            if (sub) {
                await unsubscribePush(sub.endpoint)
                await sub.unsubscribe()
            }
            this.setState({ subscribed: false })
            toast.success(getString('push.unsubscribed'))
        } catch (error) {
            toast.error((error as Error).message || getString('push.unsubscribe_failed'))
        } finally {
            this.setState({ busy: false })
        }
    }

    onTest = async () => {
        if (this.state.busy) return
        this.setState({ busy: true })
        try {
            await sendPushTest(this.props.clientId)
            toast.success(getString('push.test_sent'))
        } catch (error) {
            toast.error((error as Error).message || getString('push.test_failed'))
        } finally {
            this.setState({ busy: false })
        }
    }

    onCreateRule = async () => {
        const { selectedMetric, thresholdMinutes, cooldownMinutes, label } = this.state
        if (!selectedMetric) return
        if (thresholdMinutes <= 0 || cooldownMinutes <= 0) {
            toast.error(getString('push.invalid_threshold'))
            return
        }
        this.setState({ busy: true })
        try {
            await createPushRule({
                clientId: this.props.clientId,
                metric: selectedMetric,
                thresholdSeconds: Math.round(thresholdMinutes * 60),
                cooldownMinutes: Math.round(cooldownMinutes),
                label: label.trim() || undefined,
            })
            const rules = (await listPushRules(this.props.clientId)).data
            this.setState({ rules, label: '' })
            toast.success(getString('push.rule_created'))
        } catch (error) {
            toast.error((error as Error).message || getString('push.rule_create_failed'))
        } finally {
            this.setState({ busy: false })
        }
    }

    onToggleRule = async (rule: PushRule) => {
        this.setState({ busy: true })
        try {
            await updatePushRule(rule.id, { enabled: !rule.enabled })
            const rules = (await listPushRules(this.props.clientId)).data
            this.setState({ rules })
        } catch (error) {
            toast.error((error as Error).message)
        } finally {
            this.setState({ busy: false })
        }
    }

    onDeleteRule = async (rule: PushRule) => {
        if (!window.confirm(getString('push.delete_confirm'))) return
        this.setState({ busy: true })
        try {
            await deletePushRule(rule.id)
            const rules = (await listPushRules(this.props.clientId)).data
            this.setState({ rules })
        } catch (error) {
            toast.error((error as Error).message)
        } finally {
            this.setState({ busy: false })
        }
    }

    render() {
        const { loading, supported } = this.state
        const monitoringEnabled = this.props.monitoringEnabled !== false
        if (loading) return null
        if (!supported) return <div className="bg-yellow-100 dark:bg-yellow-900/30 p-4 rounded-xl mb-4">{getString('push.not_supported')}</div>

        return (
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl shadow-md p-4">
                <div className="text-lg font-semibold dark:text-white mb-3">{getString('push.title')}</div>
                {!monitoringEnabled && <div className="mb-4 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-900 dark:text-yellow-200 rounded-lg px-3 py-2 text-sm">{getString('monitoring.required_for_push')}</div>}
                {this.renderSubscriptionControls(monitoringEnabled)}
                {this.renderRuleBuilder(monitoringEnabled)}
                {this.renderRules()}
            </div>
        )
    }

    private renderSubscriptionControls(monitoringEnabled: boolean) {
        const { subscribed, busy } = this.state
        return (
            <div className="flex flex-wrap gap-2 mb-4">
                {!subscribed ? (
                    <button disabled={busy} onClick={this.onSubscribe} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                        {getString('push.subscribe')}
                    </button>
                ) : (
                    <button disabled={busy} onClick={this.onUnsubscribe} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
                        {getString('push.unsubscribe')}
                    </button>
                )}
                <button disabled={busy || !subscribed || !monitoringEnabled} onClick={this.onTest} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                    {getString('push.test')}
                </button>
                <div className="self-center text-sm text-gray-600 dark:text-gray-300">
                    {subscribed ? getString('push.subscribed_status') : getString('push.unsubscribed_status')}
                </div>
            </div>
        )
    }

    private renderRuleBuilder(monitoringEnabled: boolean) {
        const { metrics, selectedMetric, thresholdMinutes, cooldownMinutes, label, busy, subscribed } = this.state
        return (
            <div className="border dark:border-gray-700 rounded-lg p-3 mb-4">
                <div className="font-semibold dark:text-white mb-2">{getString('push.create_rule')}</div>
                <div className="grid md:grid-cols-4 gap-2">
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">{getString('push.metric')}</label>
                        <select
                            value={selectedMetric || ''}
                            onChange={e => this.setState({ selectedMetric: e.target.value as PushMetric })}
                            className="w-full px-3 py-2 border dark:border-gray-700 rounded dark:bg-gray-700 dark:text-white"
                        >
                            {metrics.map(metric => (
                                <option key={metric.value} value={metric.value}>
                                    {this.getMetricLabel(metric.value)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">{getString('push.threshold_minutes')}</label>
                        <input
                            type="number"
                            min={1}
                            value={thresholdMinutes}
                            onChange={e => this.setState({ thresholdMinutes: Number(e.target.value) || 1 })}
                            className="w-full px-3 py-2 border dark:border-gray-700 rounded dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">{getString('push.cooldown_minutes')}</label>
                        <input
                            type="number"
                            min={1}
                            value={cooldownMinutes}
                            onChange={e => this.setState({ cooldownMinutes: Number(e.target.value) || 1 })}
                            className="w-full px-3 py-2 border dark:border-gray-700 rounded dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">{getString('push.custom_label')}</label>
                        <input
                            type="text"
                            maxLength={160}
                            value={label}
                            onChange={e => this.setState({ label: e.target.value })}
                            className="w-full px-3 py-2 border dark:border-gray-700 rounded dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                </div>
                <div className="mt-2">
                    <button disabled={busy || !subscribed || !monitoringEnabled} onClick={this.onCreateRule} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                        {getString('push.add_rule')}
                    </button>
                </div>
            </div>
        )
    }

    private renderRules() {
        const { rules, metrics, busy } = this.state
        const metricMap = new Map(metrics.map(m => [m.value, this.getMetricLabel(m.value)]))
        if (rules.length === 0) return <div className="text-sm text-gray-600 dark:text-gray-300">{getString('push.no_rules')}</div>
        return (
            <div className="space-y-2">
                {rules.map(rule => (
                    <div key={rule.id} className="border dark:border-gray-700 rounded p-3 flex flex-wrap gap-2 items-center">
                        <div className="flex-1 min-w-72 text-sm dark:text-white">
                            <div className="font-semibold">{rule.label || metricMap.get(rule.metric) || rule.metric}</div>
                            <div className="text-gray-600 dark:text-gray-300">
                                {getString('push.rule_line', metricMap.get(rule.metric) || rule.metric, String(Math.round(rule.thresholdSeconds / 60)), String(rule.cooldownMinutes))}
                            </div>
                        </div>
                        <button
                            disabled={busy}
                            onClick={() => this.onToggleRule(rule)}
                            className={`px-3 py-1 rounded text-white ${rule.enabled ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'} disabled:opacity-50`}
                        >
                            {rule.enabled ? getString('push.disable_rule') : getString('push.enable_rule')}
                        </button>
                        <button disabled={busy} onClick={() => this.onDeleteRule(rule)} className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                            {getString('common.delete')}
                        </button>
                    </div>
                ))}
            </div>
        )
    }
}

export default PushRulesPanel
